// LegalManager.tsx
import React, { useEffect, useState } from 'react';
import {
    LegalDocType,
    LegalDocument,
    fetchLatestLegalDoc,
    publishLegal,
} from '../../../../api/legal';
import '../../../../../src/assets/styles/legal-manager.scss';

type LegalFormState = {
    locale: string;
    title: string;
    html: string;
    body_text: string;
};

function emptyForm(): LegalFormState {
    return {
        locale: 'pl',
        title: '',
        html: '',
        body_text: '',
    };
}

function formatDateTime(value?: string | null) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
}

export default function LegalManager() {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [activeDoc, setActiveDoc] = useState<LegalDocType>('TERMS');

    const [termsDoc, setTermsDoc] = useState<LegalDocument | null>(null);
    const [privacyDoc, setPrivacyDoc] = useState<LegalDocument | null>(null);

    const [termsForm, setTermsForm] = useState<LegalFormState>(emptyForm);
    const [privacyForm, setPrivacyForm] = useState<LegalFormState>(emptyForm);

    // globalne wymaganie ponownej akceptacji przy publikacji
    const [requiresReaccept, setRequiresReaccept] = useState<boolean>(true);

    const load = async () => {
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const [terms, privacy] = await Promise.allSettled([
                fetchLatestLegalDoc('TERMS'),
                fetchLatestLegalDoc('PRIVACY'),
            ]);

            if (terms.status === 'fulfilled') {
                setTermsDoc(terms.value);
                setTermsForm({
                    locale: terms.value.locale || 'pl',
                    title: terms.value.title || '',
                    html: terms.value.html || '',
                    body_text: terms.value.body_text || '',
                });
            } else {
                setTermsDoc(null);
                setTermsForm(emptyForm());
            }

            if (privacy.status === 'fulfilled') {
                setPrivacyDoc(privacy.value);
                setPrivacyForm({
                    locale: privacy.value.locale || 'pl',
                    title: privacy.value.title || '',
                    html: privacy.value.html || '',
                    body_text: privacy.value.body_text || '',
                });
            } else {
                setPrivacyDoc(null);
                setPrivacyForm(emptyForm());
            }

            // spróbuj przejąć requires_reaccept z ostatniego dokumentu
            const req =
                (terms.status === 'fulfilled' && terms.value.requires_reaccept) ||
                (privacy.status === 'fulfilled' && privacy.value.requires_reaccept) ||
                false;
            setRequiresReaccept(req);
        } catch (e: any) {
            setError(e?.error || e?.message || 'Nie udało się pobrać dokumentów');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const currentForm = activeDoc === 'TERMS' ? termsForm : privacyForm;
    const currentDoc = activeDoc === 'TERMS' ? termsDoc : privacyDoc;

    const updateForm = (field: keyof LegalFormState, value: string) => {
        if (activeDoc === 'TERMS') {
            setTermsForm((f) => ({ ...f, [field]: value }));
        } else {
            setPrivacyForm((f) => ({ ...f, [field]: value }));
        }
    };

    const onSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const payload = {
                change: 'BOTH' as const,
                requires_reaccept: requiresReaccept,
                terms: {
                    locale: termsForm.locale || 'pl',
                    title: termsForm.title,
                    html: termsForm.html,
                    body_text: termsForm.body_text,
                },
                privacy: {
                    locale: privacyForm.locale || 'pl',
                    title: privacyForm.title,
                    html: privacyForm.html,
                    body_text: privacyForm.body_text,
                },
            };

            const res = await publishLegal(payload);

            // zaktualizuj metadane (wersja / data publikacji) jeżeli przyszły w odpowiedzi
            if (res.terms) {
                setTermsDoc((prev) =>
                    prev
                        ? {
                            ...prev,
                            version: res.terms!.version,
                            published_at: res.terms!.published_at,
                            locale: res.terms!.locale,
                            title: res.terms!.title,
                        }
                        : ({
                            id: res.terms!.id,
                            doc_type: 'TERMS',
                            version: res.terms!.version,
                            published_at: res.terms!.published_at,
                            requires_reaccept: requiresReaccept,
                            locale: res.terms!.locale,
                            title: res.terms!.title,
                            html: termsForm.html,
                            body_text: termsForm.body_text,
                        } as LegalDocument),
                );
            }

            if (res.privacy) {
                setPrivacyDoc((prev) =>
                    prev
                        ? {
                            ...prev,
                            version: res.privacy!.version,
                            published_at: res.privacy!.published_at,
                            locale: res.privacy!.locale,
                            title: res.privacy!.title,
                        }
                        : ({
                            id: res.privacy!.id,
                            doc_type: 'PRIVACY',
                            version: res.privacy!.version,
                            published_at: res.privacy!.published_at,
                            requires_reaccept: requiresReaccept,
                            locale: res.privacy!.locale,
                            title: res.privacy!.title,
                            html: privacyForm.html,
                            body_text: privacyForm.body_text,
                        } as LegalDocument),
                );
            }

            setSuccess('Zapisano nowe wersje dokumentów.');
        } catch (e: any) {
            setError(e?.error || e?.message || 'Błąd przy zapisie dokumentów');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="legal-wrap">
            <div className="legal-head">
                <h2>Regulaminy i polityka prywatności</h2>
                <div className="legal-head-actions">
                    <button className="btn-ghost" onClick={load} disabled={loading || saving}>
                        Odśwież
                    </button>
                    <button className="btn-primary" onClick={onSave} disabled={saving}>
                        {saving ? 'Zapisywanie…' : 'Zapisz nową wersję'}
                    </button>
                </div>
            </div>

            {(loading || error || success) && (
                <div className="legal-states">
                    {loading && <div className="state muted">Ładowanie…</div>}
                    {error && <div className="state error">{error}</div>}
                    {success && <div className="state success">{success}</div>}
                </div>
            )}

            <div className="legal-layout">
                {/* Lista dokumentów */}
                <aside className="legal-sidebar">
                    <h3 className="sidebar-title">Dokumenty</h3>
                    <ul className="legal-list" role="tablist">
                        <li
                            className={`legal-item ${activeDoc === 'TERMS' ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeDoc === 'TERMS'}
                            onClick={() => setActiveDoc('TERMS')}
                        >
                            <div className="doc-name">Regulamin serwisu</div>
                            {termsDoc ? (
                                <div className="doc-meta">
                                    v{termsDoc.version} • {formatDateTime(termsDoc.published_at)}
                                </div>
                            ) : (
                                <div className="doc-meta muted">Brak opublikowanej wersji</div>
                            )}
                        </li>
                        <li
                            className={`legal-item ${activeDoc === 'PRIVACY' ? 'active' : ''}`}
                            role="tab"
                            aria-selected={activeDoc === 'PRIVACY'}
                            onClick={() => setActiveDoc('PRIVACY')}
                        >
                            <div className="doc-name">Polityka prywatności</div>
                            {privacyDoc ? (
                                <div className="doc-meta">
                                    v{privacyDoc.version} • {formatDateTime(privacyDoc.published_at)}
                                </div>
                            ) : (
                                <div className="doc-meta muted">Brak opublikowanej wersji</div>
                            )}
                        </li>
                    </ul>

                    <div className="legal-global">
                        <label className="checkbox-row">
                            <input
                                type="checkbox"
                                checked={requiresReaccept}
                                onChange={(e) => setRequiresReaccept(e.target.checked)}
                            />
                            <span>Nowe wersje wymagają ponownej akceptacji</span>
                        </label>
                        <p className="hint">
                            Przy zapisie powstaną nowe wersje dokumentów, a dotychczasowe trafią do historii.
                        </p>
                    </div>
                </aside>

                {/* Edytor wybranego dokumentu */}
                <section className="legal-editor" role="tabpanel">
                    <div className="legal-card">
                        <div className="legal-card-head">
                            <h3>
                                {activeDoc === 'TERMS' ? 'Regulamin serwisu' : 'Polityka prywatności'} – edycja
                            </h3>
                            <div className="meta">
                                <span>
                                    Wersja:{' '}
                                    {currentDoc ? (
                                        <strong>v{currentDoc.version}</strong>
                                    ) : (
                                        <span className="muted">brak</span>
                                    )}
                                </span>
                                <span>
                                    Opublikowano:{' '}
                                    {currentDoc ? (
                                        <strong>{formatDateTime(currentDoc.published_at)}</strong>
                                    ) : (
                                        <span className="muted">—</span>
                                    )}
                                </span>
                            </div>
                        </div>

                        <div className="form-grid">
                            <div className="fi">
                                <label>Język (locale)</label>
                                <input
                                    value={currentForm.locale}
                                    onChange={(e) => updateForm('locale', e.target.value)}
                                />
                            </div>
                            <div className="fi">
                                <label>Tytuł dokumentu</label>
                                <input
                                    value={currentForm.title}
                                    onChange={(e) => updateForm('title', e.target.value)}
                                />
                            </div>
                            <div className="fi fi-col">
                                <label>Treść HTML</label>
                                <textarea
                                    className="code-input"
                                    value={currentForm.html}
                                    onChange={(e) => updateForm('html', e.target.value)}
                                    placeholder="<h1>Regulamin</h1>..."
                                />
                            </div>
                            <div className="fi fi-col">
                                <label>Treść tekstowa (plain text)</label>
                                <textarea
                                    value={currentForm.body_text}
                                    onChange={(e) => updateForm('body_text', e.target.value)}
                                    placeholder="Wersja tekstowa regulaminu / polityki..."
                                />
                            </div>
                        </div>

                        <div className="actions-row">
                            <button className="btn-primary" onClick={onSave} disabled={saving}>
                                {saving ? 'Zapisywanie…' : 'Zapisz nową wersję (oba dokumenty)'}
                            </button>
                            <button
                                className="btn-ghost"
                                type="button"
                                onClick={load}
                                disabled={loading || saving}
                            >
                                Odrzuć zmiany
                            </button>
                        </div>
                    </div>

                    {/* Podgląd HTML (opcjonalny, bardzo prosty) */}
                    <div className="legal-preview-card">
                        <h4>Podgląd HTML</h4>
                        {currentForm.html ? (
                            <div
                                className="preview-html"
                                dangerouslySetInnerHTML={{ __html: currentForm.html }}
                            />
                        ) : (
                            <div className="state muted">Brak treści HTML – wprowadź tekst powyżej.</div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
